import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import { useSessionHeartbeat } from '../hooks/useSessionHeartbeat';
import InactivityWarning from './InactivityWarning';
import Dashboard from '../pages/Dashboard';
import OrderReports from '../pages/OrderReports';
import CreateOrder from '../pages/CreateOrderStepper';
import EditOrder from '../pages/EditOrder';
import OrderDetail from '../pages/OrderDetailNew';
import Approvals from '../pages/Approvals';
import ApprovalReports from '../pages/ApprovalReports';
import ApprovalDetail from '../pages/ApprovalDetail';
import EditApproval from '../pages/EditApproval';
import CreateApprovalStepper from '../pages/CreateApprovalStepper';
import CreateBookingStepper from '../pages/CreateBookingStepper';
import EditBooking from '../pages/EditBooking';
import AdminBookingTypes from '../pages/AdminBookingTypes';
import AdminSettings from '../pages/AdminSettings';
import BookingDetail from '../pages/BookingDetail';
import PaymentReminders from '../pages/PaymentReminders';
import CreateKittyEnrollment from '../pages/CreateKittyEnrollment';
import KittySchemes from '../pages/KittySchemes';
import CreateScheme from '../pages/CreateScheme';
import CreateKittyEnrollmentStepper from '../pages/CreateKittyEnrollmentStepper';
import KittyEnrollmentDetail from '../pages/KittyEnrollmentDetail';
import KittyReports from '../pages/KittyReportsNew';
import Customers from '../pages/Customers';
import Vendors from '../pages/Vendors';
import AdminApprovals from '../pages/AdminApprovals';
import AdminSignatureNames from '../pages/AdminSignatureNames';
import MySubmissions from '../pages/MySubmissions';
import ChangeRequests from '../pages/ChangeRequests';
import RateBook from '../pages/RateBook';
import SelectionsList from '../pages/SelectionsList';
import CreateSelectionStepper from '../pages/CreateSelectionStepper';
import EditSelection from '../pages/EditSelection';
import SelectionDetail from '../pages/SelectionDetail';
import PaymentLedger from '../pages/PaymentLedger';
import ItemTracker from '../pages/ItemTracker';

const AppContent = ({ user, setUser, handleLogin, handleLogout }) => {
  // Inactivity timeout tracking (now inside Router context)
  const { showWarning, handleStayLoggedIn, handleLogout: handleInactivityLogout } = useInactivityTimeout(user, () => {
    // Clear session data on timeout
    sessionStorage.removeItem('token');
    setUser(null);
  });

  // Session heartbeat - detects if user logged in from another device
  useSessionHeartbeat(user);

  return (
    <>
      {/* Inactivity Warning Modal */}
      <InactivityWarning 
        isOpen={showWarning}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleInactivityLogout}
      />
      
      <Routes>
        {/* Public Routes */}
        <Route path="/ratebook" element={<RateBook user={user} onLogin={handleLogin} onLogout={handleLogout} />} />
        
        {/* Protected Routes - redirect to ratebook if not logged in */}
        {!user ? (
          <Route path="*" element={<Navigate to="/ratebook" />} />
        ) : (
          <>
            <Route path="/" element={<Dashboard user={user} onLogout={handleLogout} />} />
            <Route path="/orders/reports" element={<OrderReports user={user} onLogout={handleLogout} />} />
            <Route path="/orders/create" element={<CreateOrder user={user} onLogout={handleLogout} />} />
            <Route path="/orders/edit/:orderId" element={<EditOrder user={user} onLogout={handleLogout} />} />
            <Route path="/orders/:orderId" element={<OrderDetail user={user} onLogout={handleLogout} />} />
            <Route path="/approvals" element={<Approvals user={user} onLogout={handleLogout} />} />
            <Route path="/approvals/reports" element={<ApprovalReports user={user} onLogout={handleLogout} />} />
            <Route path="/approvals/create" element={<CreateApprovalStepper user={user} onLogout={handleLogout} />} />
            <Route path="/approvals/edit/:approvalId" element={<EditApproval user={user} onLogout={handleLogout} />} />
            <Route path="/approvals/:approvalId" element={<ApprovalDetail user={user} onLogout={handleLogout} />} />
            <Route path="/bookings/create" element={<CreateBookingStepper user={user} onLogout={handleLogout} />} />
            <Route path="/bookings/edit/:bookingId" element={<EditBooking user={user} onLogout={handleLogout} />} />
            <Route path="/bookings/:bookingId" element={<BookingDetail user={user} onLogout={handleLogout} />} />
            <Route path="/selections" element={<SelectionsList user={user} onLogout={handleLogout} />} />
            <Route path="/selections/create" element={<CreateSelectionStepper user={user} onLogout={handleLogout} />} />
            <Route path="/selections/:selectionId/edit" element={<EditSelection user={user} onLogout={handleLogout} />} />
            <Route path="/selections/:selectionId" element={<SelectionDetail user={user} onLogout={handleLogout} />} />
            <Route path="/payment-reminders" element={<PaymentReminders user={user} onLogout={handleLogout} />} />
            <Route path="/admin/booking-types" element={<AdminBookingTypes user={user} onLogout={handleLogout} />} />
            <Route path="/admin/settings" element={<AdminSettings user={user} onLogout={handleLogout} />} />
            <Route path="/kitty" element={<KittyReports user={user} onLogout={handleLogout} />} />
            <Route path="/kitty/enroll" element={<CreateKittyEnrollment user={user} onLogout={handleLogout} />} />
            <Route path="/kitty/schemes" element={<KittySchemes user={user} onLogout={handleLogout} />} />
            <Route path="/kitty/schemes/create" element={<CreateScheme user={user} onLogout={handleLogout} />} />
            <Route path="/kitty/schemes/:schemeId/edit" element={<CreateScheme user={user} onLogout={handleLogout} />} />
            <Route path="/kitty/enrollments/create" element={<CreateKittyEnrollmentStepper user={user} onLogout={handleLogout} />} />
            <Route path="/kitty/enrollments/:enrollmentId" element={<KittyEnrollmentDetail user={user} onLogout={handleLogout} />} />
            <Route path="/kitty/reports" element={<KittyReports user={user} onLogout={handleLogout} />} />
            <Route path="/customers" element={<Customers user={user} onLogout={handleLogout} />} />
            <Route path="/vendors" element={<Vendors user={user} onLogout={handleLogout} />} />
            <Route path="/admin/approvals" element={<AdminApprovals user={user} onLogout={handleLogout} />} />
            <Route path="/admin/signature-names" element={<AdminSignatureNames user={user} onLogout={handleLogout} />} />
            <Route path="/submissions/my-all" element={<MySubmissions user={user} onLogout={handleLogout} />} />
            <Route path="/change-requests" element={<ChangeRequests user={user} onLogout={handleLogout} />} />
            <Route path="/reports/payment-ledger" element={<PaymentLedger user={user} onLogout={handleLogout} />} />
            <Route path="/reports/item-tracker" element={<ItemTracker user={user} onLogout={handleLogout} />} />
            <Route path="*" element={<Navigate to="/" />} />
          </>
        )}
      </Routes>
    </>
  );
};

export default AppContent;
